/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAlert } from '../context/alert.context';
// eslint-disable-next-line import/no-cycle
import { useUserContext } from '../context/user.context';
import { refreshAccessToken } from '../service/auth.service';

const useFetch = (
  url: string,
  { successAlert, skipFailAlert }: { successAlert?: boolean; skipFailAlert?: boolean } = {
    successAlert: false,
    skipFailAlert: false,
  },
) => {
  const navigate = useNavigate();
  const user = useUserContext();
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>('');
  const [result, setResult] = useState<any>(null);
  const abortController = useRef<any>(null);

  const doFetch = useCallback(
    async (
      options?: RequestInit & {
        newURL?: string;
      },
      isRetry?: boolean,
    ): Promise<any> => {
      setLoading(true);
      setError('');
      if (abortController.current) abortController.current.abort();

      try {
        abortController.current = new AbortController();
        const res = await fetch(options?.newURL || url, {
          signal: abortController.current.signal,
          ...options,
        });

        // 401 and not already a retry — try refreshing
        if (res.status === 401 && !isRetry) {
          const newToken = await refreshAccessToken();

          if (newToken) {
            const retryOptions = {
              ...options,
              headers: {
                ...options?.headers,
                Authorization: `Bearer ${newToken}`,
              },
            };

            return await doFetch(retryOptions, true);
          }
          // Refresh failed — fall through to error handling
        }

        if (res.headers.get('Content-Type') === 'application/pdf') {
          if (!res.ok) {
            throw new Error('Download failed!');
          }
          const blob = await res.blob();
          setResult(blob);
          return blob;
        }

        const resData = await res.json();
        if (res.status === 403) {
          user?.logout();
        }

        if (
          !resData ||
          resData.status === 'error' ||
          resData.status === 'fail' ||
          resData.statusCode === 404
        ) {
          throw new Error(resData.message);
        }
        setError('');
        setResult({ ...resData });
        setLoading(false);
        if (resData.data?.redirectURL) {
          navigate(resData.data.redirectURL);
        }
        if (successAlert) {
          setAlert({
            message: resData.message,
            status: 'success',
          });
        }
        return resData;
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          setError(err);
          setResult(null);
          setLoading(false);
          if (!skipFailAlert) {
            setAlert({
              message: err.message,
              status: 'error',
            });
          }
        } else {
          return 'aborted';
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alert, user?.logout, setAlert, url],
  );

  return { loading, error, result, doFetch, abortController };
};

export default useFetch;
